import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './PatientsModal.module.css';
import AddPatientModal from './AddPatientModal';
import { 
  FaUsers, 
  FaPlus, 
  FaTimes, 
  FaEdit, 
  FaTrash, 
  FaSearch,
  FaPills,
  FaUserCheck,
  FaUserClock,
  FaUserTimes,
  FaClipboardList,
  FaHome,
  FaCalendarAlt,
  FaChartBar,
  FaEye,
  FaFilter,
  FaDownload
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';

// Patient status options
const PATIENT_STATUSES = {
  planned: { label: 'Planlanan', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
  screened: { label: 'Screen Edilen', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.15)' },
  screenFail: { label: 'Screen Fail', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.15)' },
  enrolled: { label: 'Dahil Edilen', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.15)' },
  active: { label: 'Aktif', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.15)' },
  onMedication: { label: 'İlaç Alan', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.15)' },
  completed: { label: 'Tamamlanan', color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.15)' },
  withdrawn: { label: 'Çekilen', color: '#dc2626', bgColor: 'rgba(220, 38, 38, 0.15)' }
};

const PatientsModal = ({ project, onClose }) => {
  const { isDarkMode } = useTheme();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);

  // Calculate statistics
  const statistics = {
    total: patients.length,
    planned: patients.filter(p => p.status === 'planned').length,
    screened: patients.filter(p => p.status === 'screened').length,
    screenFail: patients.filter(p => p.status === 'screenFail').length,
    enrolled: patients.filter(p => p.status === 'enrolled').length,
    active: patients.filter(p => p.status === 'active').length,
    onMedication: patients.filter(p => p.status === 'onMedication').length,
    completed: patients.filter(p => p.status === 'completed').length,
    withdrawn: patients.filter(p => p.status === 'withdrawn').length,
    withHomeVisit: patients.filter(p => p.hasHomeVisit).length
  };

  // Calculate active patients by visit number
  const activePatientsByVisit = patients
    .filter(p => p.status === 'active' || p.status === 'onMedication')
    .reduce((acc, patient) => {
      const visitNum = patient.currentVisitNumber || 1;
      acc[visitNum] = (acc[visitNum] || 0) + 1;
      return acc;
    }, {});

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const patientsQuery = query(
        collection(db, 'Patients'),
        where('projectId', '==', project.id)
      );
      const querySnapshot = await getDocs(patientsQuery);
      
      const patientsData = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      // Sort client-side by createdAt (descending) to avoid needing a composite index
      patientsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return dateB - dateA;
      });

      setPatients(patientsData);
      setFilteredPatients(patientsData);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Hasta bilgileri yüklenirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [project.id]);

  // Filter patients based on search and status
  useEffect(() => {
    let filtered = patients;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(patient =>
        patient.patientId?.toLowerCase().includes(searchLower) ||
        patient.notes?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(patient => patient.status === statusFilter);
    }

    setFilteredPatients(filtered);
  }, [patients, searchTerm, statusFilter]);

  const handleDeletePatient = async (patientId) => {
    try {
      await deleteDoc(doc(db, 'Patients', patientId));
      toast.success('Hasta başarıyla silindi');
      setShowDeleteConfirm(null);
      fetchPatients();
    } catch (error) {
      console.error('Error deleting patient:', error);
      toast.error('Hasta silinirken hata oluştu');
    }
  };

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setShowAddModal(true);
  };

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setShowDetailView(true);
  };

  const handleExportExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Hasta Listesi');

      // Define columns
      worksheet.columns = [
        { header: 'Hasta ID', key: 'patientId', width: 15 },
        { header: 'Durum', key: 'status', width: 15 },
        { header: 'Kayıt Tarihi', key: 'enrollmentDate', width: 15 },
        { header: 'İlk Doz Tarihi', key: 'firstDoseDate', width: 15 },
        { header: 'Mevcut Visit', key: 'currentVisitNumber', width: 12 },
        { header: 'Home Visit', key: 'hasHomeVisit', width: 12 },
        { header: 'Home Visit Tarihleri', key: 'homeVisitDates', width: 30 },
        { header: 'Notlar', key: 'notes', width: 40 }
      ];

      // Style header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF3182CE' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // Add data
      patients.forEach(patient => {
        worksheet.addRow({
          patientId: patient.patientId,
          status: PATIENT_STATUSES[patient.status]?.label || patient.status,
          enrollmentDate: patient.enrollmentDate || '-',
          firstDoseDate: patient.firstDoseDate || '-',
          currentVisitNumber: patient.currentVisitNumber || '-',
          hasHomeVisit: patient.hasHomeVisit ? 'Evet' : 'Hayır',
          homeVisitDates: patient.homeVisitDates?.join(', ') || '-',
          notes: patient.notes || '-'
        });
      });

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.titleShort || 'Proje'}_Hasta_Listesi.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Excel dosyası oluşturulurken hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR');
    } catch {
      return dateString;
    }
  };

  return (
    <div className={`${styles.modalBackdrop} ${isDarkMode ? styles.darkMode : ''}`}>
      <div className={`${styles.modal} ${isDarkMode ? styles.darkMode : ''}`}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.titleSection}>
            <FaUsers className={styles.modalIcon} />
            <div>
              <h2 className={styles.modalTitle}>Hasta Bilgileri</h2>
              <p className={styles.projectName}>{project.titleShort || project.customer}</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* Statistics Section */}
          <div className={styles.statisticsSection}>
            <h3 className={styles.sectionTitle}>
              <FaChartBar className={styles.sectionIcon} />
              Hasta Özeti
            </h3>
            
            <div className={styles.statsGrid}>
              {/* Main Stats */}
              <div className={`${styles.statCard} ${styles.totalCard}`}>
                <div className={styles.statIcon}><FaUsers /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.total}</span>
                  <span className={styles.statLabel}>Toplam Hasta</span>
                </div>
              </div>

              <div className={`${styles.statCard} ${styles.enrolledCard}`}>
                <div className={styles.statIcon}><FaClipboardList /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.enrolled}</span>
                  <span className={styles.statLabel}>Dahil Edilen</span>
                </div>
              </div>

              <div className={`${styles.statCard} ${styles.activeCard}`}>
                <div className={styles.statIcon}><FaUserCheck /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.active}</span>
                  <span className={styles.statLabel}>Aktif Hasta</span>
                </div>
              </div>

              <div className={`${styles.statCard} ${styles.medicationCard}`}>
                <div className={styles.statIcon}><FaPills /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.onMedication}</span>
                  <span className={styles.statLabel}>İlaç Alan</span>
                </div>
              </div>

              <div className={`${styles.statCard} ${styles.plannedCard}`}>
                <div className={styles.statIcon}><FaUserClock /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.planned}</span>
                  <span className={styles.statLabel}>Planlanan</span>
                </div>
              </div>

              <div className={`${styles.statCard} ${styles.screenedCard}`}>
                <div className={styles.statIcon}><FaClipboardList /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.screened}</span>
                  <span className={styles.statLabel}>Screen Edilen</span>
                </div>
              </div>

              <div className={`${styles.statCard} ${styles.screenFailCard}`}>
                <div className={styles.statIcon}><FaUserTimes /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.screenFail}</span>
                  <span className={styles.statLabel}>Screen Fail</span>
                </div>
              </div>

              <div className={`${styles.statCard} ${styles.homeVisitCard}`}>
                <div className={styles.statIcon}><FaHome /></div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue}>{statistics.withHomeVisit}</span>
                  <span className={styles.statLabel}>Home Visit</span>
                </div>
              </div>
            </div>

            {/* Active Patients by Visit */}
            {Object.keys(activePatientsByVisit).length > 0 && (
              <div className={styles.visitDistribution}>
                <h4 className={styles.visitTitle}>
                  <FaCalendarAlt className={styles.visitIcon} />
                  Aktif Hastaların Visit Dağılımı
                </h4>
                <div className={styles.visitBadges}>
                  {Object.entries(activePatientsByVisit)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([visitNum, count]) => (
                      <div key={visitNum} className={styles.visitBadge}>
                        <span className={styles.visitNumber}>Visit {visitNum}</span>
                        <span className={styles.visitCount}>{count} hasta</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions Bar */}
          <div className={styles.actionsBar}>
            <div className={styles.searchSection}>
              <div className={styles.searchBox}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Hasta ID ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                {searchTerm && (
                  <button 
                    className={styles.clearSearch}
                    onClick={() => setSearchTerm('')}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              <div className={styles.filterBox}>
                <FaFilter className={styles.filterIcon} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">Tüm Durumlar</option>
                  {Object.entries(PATIENT_STATUSES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.actionButtons}>
              <button
                className={styles.exportButton}
                onClick={handleExportExcel}
                disabled={patients.length === 0}
              >
                <FaDownload /> Excel İndir
              </button>
              <button
                className={styles.addButton}
                onClick={() => {
                  setEditingPatient(null);
                  setShowAddModal(true);
                }}
              >
                <FaPlus /> Yeni Hasta Ekle
              </button>
            </div>
          </div>

          {/* Patients Table */}
          <div className={styles.tableContainer}>
            {loading ? (
              <div className={styles.loadingIndicator}>
                <div className={styles.loadingSpinner}></div>
                <p>Hastalar yükleniyor...</p>
              </div>
            ) : filteredPatients.length > 0 ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Hasta ID</th>
                    <th>Durum</th>
                    <th>Kayıt Tarihi</th>
                    <th>İlk Doz Tarihi</th>
                    <th>Mevcut Visit</th>
                    <th>Home Visit</th>
                    <th className={styles.actionsColumn}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPatients.map(patient => (
                    <tr key={patient.id}>
                      <td className={styles.patientIdCell}>
                        <span className={styles.patientIdBadge}>{patient.patientId}</span>
                      </td>
                      <td>
                        <span 
                          className={styles.statusBadge}
                          style={{
                            color: PATIENT_STATUSES[patient.status]?.color,
                            backgroundColor: PATIENT_STATUSES[patient.status]?.bgColor
                          }}
                        >
                          {PATIENT_STATUSES[patient.status]?.label || patient.status}
                        </span>
                      </td>
                      <td>{formatDate(patient.enrollmentDate)}</td>
                      <td>{formatDate(patient.firstDoseDate)}</td>
                      <td>
                        {patient.currentVisitNumber ? (
                          <span className={styles.visitNumberBadge}>
                            Visit {patient.currentVisitNumber}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {patient.hasHomeVisit ? (
                          <span className={styles.homeVisitBadge}>
                            <FaHome /> Evet
                          </span>
                        ) : (
                          <span className={styles.noHomeVisit}>-</span>
                        )}
                      </td>
                      <td className={styles.actionsCell}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleViewDetails(patient)}
                          title="Detayları Görüntüle"
                        >
                          <FaEye />
                        </button>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleEditPatient(patient)}
                          title="Düzenle"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className={`${styles.actionBtn} ${styles.deleteBtn}`}
                          onClick={() => setShowDeleteConfirm(patient.id)}
                          title="Sil"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.noData}>
                <FaUsers className={styles.noDataIcon} />
                <p>
                  {searchTerm || statusFilter !== 'all'
                    ? 'Filtrelere uygun hasta bulunamadı.'
                    : 'Bu projeye ait hasta kaydı bulunamadı.'}
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <button
                    className={styles.addFirstButton}
                    onClick={() => {
                      setEditingPatient(null);
                      setShowAddModal(true);
                    }}
                  >
                    <FaPlus /> İlk Hastayı Ekle
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Results count */}
          {!loading && (
            <div className={styles.resultsInfo}>
              {filteredPatients.length} / {patients.length} hasta gösteriliyor
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.closeBtn} onClick={onClose}>
            Kapat
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className={styles.confirmOverlay}>
            <div className={styles.confirmModal}>
              <h3>Hastayı Sil</h3>
              <p>Bu hastayı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
              <div className={styles.confirmButtons}>
                <button
                  className={styles.confirmDelete}
                  onClick={() => handleDeletePatient(showDeleteConfirm)}
                >
                  Evet, Sil
                </button>
                <button
                  className={styles.confirmCancel}
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Patient Detail View Modal */}
        {showDetailView && selectedPatient && (
          <div className={styles.confirmOverlay}>
            <div className={styles.detailModal}>
              <div className={styles.detailHeader}>
                <h3>Hasta Detayları</h3>
                <button 
                  className={styles.detailClose}
                  onClick={() => setShowDetailView(false)}
                >
                  <FaTimes />
                </button>
              </div>
              <div className={styles.detailContent}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Hasta ID:</span>
                  <span className={styles.detailValue}>{selectedPatient.patientId}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Durum:</span>
                  <span 
                    className={styles.statusBadge}
                    style={{
                      color: PATIENT_STATUSES[selectedPatient.status]?.color,
                      backgroundColor: PATIENT_STATUSES[selectedPatient.status]?.bgColor
                    }}
                  >
                    {PATIENT_STATUSES[selectedPatient.status]?.label}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Kayıt Tarihi:</span>
                  <span className={styles.detailValue}>{formatDate(selectedPatient.enrollmentDate)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>İlk Doz Tarihi:</span>
                  <span className={styles.detailValue}>{formatDate(selectedPatient.firstDoseDate)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Mevcut Visit:</span>
                  <span className={styles.detailValue}>
                    {selectedPatient.currentVisitNumber ? `Visit ${selectedPatient.currentVisitNumber}` : '-'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Home Visit:</span>
                  <span className={styles.detailValue}>
                    {selectedPatient.hasHomeVisit ? 'Evet' : 'Hayır'}
                  </span>
                </div>
                {selectedPatient.hasHomeVisit && selectedPatient.homeVisitDates?.length > 0 && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Home Visit Tarihleri:</span>
                    <div className={styles.homeVisitDates}>
                      {selectedPatient.homeVisitDates.map((date, idx) => (
                        <span key={idx} className={styles.dateTag}>
                          {formatDate(date)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedPatient.homeVisitInfo && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Home Visit Bilgisi:</span>
                    <span className={styles.detailValue}>{selectedPatient.homeVisitInfo}</span>
                  </div>
                )}
                {selectedPatient.notes && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Notlar:</span>
                    <span className={styles.detailValue}>{selectedPatient.notes}</span>
                  </div>
                )}
              </div>
              <div className={styles.detailFooter}>
                <button
                  className={styles.editFromDetail}
                  onClick={() => {
                    setShowDetailView(false);
                    handleEditPatient(selectedPatient);
                  }}
                >
                  <FaEdit /> Düzenle
                </button>
                <button
                  className={styles.closeDetailBtn}
                  onClick={() => setShowDetailView(false)}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Patient Modal */}
        {showAddModal && (
          <AddPatientModal
            project={project}
            patient={editingPatient}
            onClose={() => {
              setShowAddModal(false);
              setEditingPatient(null);
            }}
            onSave={() => {
              setShowAddModal(false);
              setEditingPatient(null);
              fetchPatients();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PatientsModal;
